import React, { useEffect, useState, useMemo } from 'react';
import RenderFieldBasedOnType from '../RenderFieldsBasedOnType';
import {
    Box,
    Grid,
    Typography,
    Tooltip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    CircularProgress,
    List,
    ListItem,
    ListItemText,
    Divider,
    Avatar
} from '@material-ui/core';
import RotateLeftIcon from '@material-ui/icons/RotateLeft';
import HistoryIcon from '@material-ui/icons/History';
import nameInitials from '../../../name-initials';
import { toast } from 'react-toastify';
import { Helpers } from "dsilo-ui-components";
import { makeStyles } from '@material-ui/core/styles';
import { useCardStyles, useCommonStyles, useInputStyles } from './style';
import { checkConditions, getTierDateErrorForElement, DEFAULT_TIER_DATE_RULES, DEFAULT_TIER_DATE_VALIDATION_MODE, formatDecisionHistoryDisplayTime, isApiFetchSelectElement, isApiFetchSelectValueEmpty } from '../../utils';
import { getNegotiationStatusOptions, NEGOTIATION_STATUS_FIELD, resolveNegotiationConfigFromBlocks } from '../../utils/negotiationStatusRules';

const useDialogStyles = makeStyles((theme) => ({
    dialogPaper: {
        borderRadius: 0, // Sharp, angular corners as per Figma
        minWidth: 600,
        maxWidth: 700,
    },
    dialogTitle: {
        padding: theme.spacing(2, 2, 1.5),
    },
    dialogContent: {
        padding: theme.spacing(0, 2, 2),
        paddingTop: theme.spacing(2),
        maxHeight: 400,
        overflowY: 'auto',
    },
    listRoot: {
        paddingTop: 0,
        paddingBottom: 0,
    },
    listItem: {
        alignItems: 'flex-start',
        paddingTop: theme.spacing(1.5),
        paddingBottom: theme.spacing(1.5),
        paddingLeft: 0,
        paddingRight: 0,
        display: 'flex',
        flexDirection: 'row',
    },
    timestamp: {
        minWidth: 180,
        fontSize: theme.typography.caption.fontSize,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(2),
        alignSelf: 'flex-start',
        paddingTop: theme.spacing(0.5),
        flexShrink: 0,
    },
    avatarContainer: {
        minWidth: 40,
        marginRight: theme.spacing(1.5),
        alignSelf: 'flex-start',
        flexShrink: 0,
    },
    avatar: {
        width: 32,
        height: 32,
        fontSize: theme.typography.caption.fontSize,
        backgroundColor: theme.palette.grey[300],
        color: theme.palette.text.primary,
        fontWeight: 500,
    },
    listItemText: {
        flex: 1,
        margin: 0,
        '& .MuiListItemText-primary': {
            marginBottom: theme.spacing(0.5),
            '& strong': {
                fontWeight: 600,
            },
        },
        '& .MuiListItemText-secondary': {
            display: 'block',
            lineHeight: 1.4,
        },
    },
    dialogActions: {
        padding: theme.spacing(2),
        justifyContent: 'flex-end',
    },
    closeButton: {
        backgroundColor: theme.palette.grey[200],
        color: theme.palette.text.primary,
        '&:hover': {
            backgroundColor: theme.palette.grey[300],
        },
    },
}));

/* For now It is static */
const defaultOptions = {}

/**
 * Tier reset: visibility and reset fields are driven by config.
 * Operators: is_equals (exact match), is_there (non-empty), any_of (value in list).
 */
function evaluateTierResetCondition(blockState, condition) {
    if (!condition) return false;
    const el = condition.selectedConditionDataElement;
    const op = condition.selectedConditionOperator;
    const raw = blockState?.[el];
    if (op === 'is_equals') {
        return raw === condition.value;
    }
    if (op === 'is_there') {
        if (raw === undefined || raw === null) return false;
        if (typeof raw === 'string') return raw.trim() !== '';
        if (Array.isArray(raw)) return raw.length > 0;
        return true;
    }
    if (op === 'any_of') {
        const list = condition.value;
        if (!Array.isArray(list)) return false;
        return list.includes(raw);
    }
    return false;
}

function tierResetConfigMatches(blockState, config) {
    const rootOk = evaluateTierResetCondition(blockState, {
        selectedConditionDataElement: config.selectedConditionDataElement,
        selectedConditionOperator: config.selectedConditionOperator,
        value: config.value,
    });
    if (!rootOk) return false;
    return (config.actionButtonShowFields || []).every((c) => evaluateTierResetCondition(blockState, c));
}

function getTierResetDecisionFieldForButton(config) {
    const fields = config.actionButtonShowFields || [];
    const dec = fields.find((f) => /decision/i.test(f.selectedConditionDataElement || ''));
    return dec?.selectedConditionDataElement;
}

function parseMaxTierFromTopTierValue(topTierVal) {
    if (topTierVal == null || topTierVal === '') return null;
    const match = String(topTierVal).match(/tier\s*(\d+)/i);
    return match ? parseInt(match[1], 10) : null;
}

/** Returns tier number from field config (tierLevel or tierN in value/name). */
function getFieldTierNumber(element) {
    if (element?.tierLevel != null) return element.tierLevel;
    const fieldKey = element?.value || element?.name || '';
    const match = String(fieldKey).match(/tier(\d+)/i);
    return match ? parseInt(match[1], 10) : null;
}

const SummaryBlock = (props) => {
    const layoutClasses = useCommonStyles()
    const inputClasses = useInputStyles();
    const cardClasses = useCardStyles();
    const dialogClasses = useDialogStyles();

    const { currentDocumentId, formData, selectedStep, block, blockState, handleCheckboxChange, onChange, showValidationErrors, auditTrail, dealHasUnsavedChanges } = props
    const { chartUniqueId, chartData} = props.data
    const { tabs } = chartData
    const TIER_RESET_CONFIG = tabs?.dealReviewDetails?.TIER_RESET_CONFIG || [];
    const dealReviewBlocks = tabs?.dealReviewDetails?.blocks;

    const negotiationConfig = useMemo(
        () => resolveNegotiationConfigFromBlocks(dealReviewBlocks),
        [dealReviewBlocks],
    );

    /** Same key as View.js hydrate effect — document fetch landed in Redux */
    const loadedSingleDocument = formData?.document?.[`${chartUniqueId}_getsingledocument`];
    const hasDealDocumentContext =
        Boolean(props?.match?.params?.id) || Boolean(loadedSingleDocument);
    const dealDocumentIdForHistory =
        currentDocumentId ||
        props?.match?.params?.id ||
        blockState?._id ||
        blockState?.id ||
        loadedSingleDocument?._id ||
        loadedSingleDocument?.id;
    const dealDocumentIdStr =
        dealDocumentIdForHistory != null && dealDocumentIdForHistory !== ''
            ? String(dealDocumentIdForHistory)
            : undefined;

    const isSaveDealReviewHistoryLoading = Boolean(
        formData?.[`${chartUniqueId}_save_deal_review_history_loading`],
    );

    const [blockHeaders, setBlockHeaders] = useState([])
    const [decisionHistoryOpen, setDecisionHistoryOpen] = useState({
        open: false,
        data: {
            value: null,
            key: null,
            label: null,
            history:null
        }
    })

    const tierResetFields = useMemo(() => {
        const resetSet = new Set();
        TIER_RESET_CONFIG.forEach((config) => {
            if (!tierResetConfigMatches(blockState, config)) return;
            const decisionField = getTierResetDecisionFieldForButton(config);
            if (decisionField) resetSet.add(decisionField);
        });
        return resetSet;
    }, [blockState, TIER_RESET_CONFIG]);

    const handleTierReset = () => {
        if (!dealDocumentIdStr) return;
        if (dealHasUnsavedChanges) {
            toast.error('Save your changes first before reset document');
            return;
        }
        props.saveDealReviewHistory({
            appId: props.match.params.appid,
            dataModelId: props.data.chartData.tabs.activities.dataModelId,
            subDataModelId: props.data.chartData.tabs.activities.subDataModelId,
            dataUniqueId: chartUniqueId + '_save_deal_review_history',
            documentId: dealDocumentIdStr,
            payload: {
               pageUrl: props.page?.url
            }
        })
    };

    const showHistory = (element) => {
        const formatToDDMMYY = (dateStr) => {
            if (!dateStr) return dateStr;
            try {
                if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
                    const parts = dateStr.split('T')[0].split('-');
                    if (parts.length === 3) {
                        return `${parts[2]}/${parts[1]}/${parts[0].slice(-2)}`;
                    }
                }
                const date = new Date(dateStr);
                if (isNaN(date.getTime())) return dateStr;
                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const year = String(date.getFullYear()).slice(-2);
                return `${day}/${month}/${year}`;
            } catch {
                return dateStr;
            }
        };

        const rawHistory = blockState[`${element.value}History`] ?? [];
        const history = element.isDate
            ? rawHistory.map((entry) => (
                entry && typeof entry === 'object' && entry.value
                    ? { ...entry, value: formatToDDMMYY(entry.value) }
                    : entry
            ))
            : rawHistory;

        setDecisionHistoryOpen({
            open: true,
            data: {
                value: element.value,
                date: element.isDate,
                key: `${element.value}History`,
                label: element.label,
                history
            }
        })
    }

    /**
     * Block config supports:
     * - dataElements, tierControlField, tierLevel (tier readOnly logic)
     * - tierDateValidation.rules (date ordering validation)
     * - tierDateValidation.mode: 'warning' (default, inline only) | 'error' (blocks save)
     * - statusTransition: auto-update negotiation status when date fields change
     * - initialDefaults: default field values when creating a new deal
     *
     * initialDefaults example (add to block.config):
     * {
     *   dealIdPrefix: 'Deal Review',
     *   dealValue: 0,
     *   needToKnowSession: 'FALSE',
     *   priority: 'Normal',
     *   negotiationStatus: 'Deal Review'
     * }
     * Omit any key to use DEFAULT_INITIAL_DEFAULTS; add custom fields to extend.
     *
     * statusTransition example (add to block.config):
     * {
     *   statusField: 'negotiationStatus',
     *   transitions: [
     *     { triggerFields: ['topTierRequired'], whenValueCleared: 'Deal Review', onlyWhenCleared: true },
     *     { triggerFields: ['tier0ApprovalDate','tier1ApprovalDate','tier2ApprovalDate'], toStatus: 'Negotiation in progress', whenValueCleared: 'Deal Review' },
     *     { triggerFields: ['approvalDate','negotiationCompletionDate'], toStatus: 'Negotiation completed', whenValueCleared: 'Negotiation in progress' }
     *   ]
     * }
     * When topTierRequired is cleared → status moves to 'Deal Review'.
     * When a top-tier approval date is entered → status moves to 'Negotiation in progress'.
     * When negotiation completion date is added → status moves to 'Negotiation completed'.
     */
    useEffect(() => {
        if (formData?.dataModel?.[selectedStep?.dataModel]) {
            let dataElements = formData.dataModel[selectedStep.dataModel].dataElements || [];
            if (dataElements?.length && block?.config?.dataElements) {
                let fdes = JSON.parse(JSON.stringify(block.config.dataElements));
                fdes = fdes.map(fde => {
                    let sde = dataElements.find(de => de.name === fde.value);
                    return sde?.name ? { ...sde, ...fde } : fde;
                });

                // Tier readOnly: driven by topTierRequired (Tier 1 / Tier 2)
                const controlEl = fdes.find(el => el.tierControlField === true);
                const controlField = controlEl?.value ?? 'topTierRequired';
                const topTierVal = blockState?.[controlField];
                const maxTier = parseMaxTierFromTopTierValue(topTierVal);

                fdes = fdes.map(element => {
                    if (element.tierControlField === true) {
                        return { ...element, readOnly: true };
                    }

                    const fieldTier = getFieldTierNumber(element);
                    if (fieldTier == null) return element;

                    if (maxTier == null) {
                        return { ...element, readOnly: true };
                    }

                    // topTierRequired "Tier 1" → tier1 fields editable; tier2 fields readOnly
                    // topTierRequired "Tier 2" → tier2 fields editable; tier1 fields readOnly
                    const tierAdjustedElement = { ...element, readOnly: fieldTier !== maxTier };
                    const fieldKey = tierAdjustedElement.value || tierAdjustedElement.name;

                    if (fieldKey === NEGOTIATION_STATUS_FIELD) {
                        return {
                            ...tierAdjustedElement,
                            options: getNegotiationStatusOptions(negotiationConfig, element.options),
                        };
                    }

                    return tierAdjustedElement;
                });

                fdes = fdes.map((element) => {
                    const fieldKey = element.value || element.name;
                    if (fieldKey !== NEGOTIATION_STATUS_FIELD || element.options?.length) {
                        return element;
                    }

                    return {
                        ...element,
                        options: getNegotiationStatusOptions(negotiationConfig, element.options),
                    };
                });

                setBlockHeaders(fdes);
            }
        }
    }, [formData?.dataModel, selectedStep?.dataModel, block?.config?.dataElements, blockState]);


    return (
        <React.Fragment>
            <div className={layoutClasses.columnContainer}>
                <Grid container spacing={1}>
                    {
                        blockHeaders?.length > 0 ? blockHeaders.map((element, index) => {
                            let isFieldReadOnly = element.readOnly;

                            const result = !element?.roleEnabled?.some(enabledRole =>
                                props?.members?.roles?.some(role => role?.name === enabledRole?.name) &&
                                enabledRole?.opertions?.includes("EDIT")
                            );

                            if (element?.roleEnabled) {
                                isFieldReadOnly = result
                            }

                            return (
                                checkConditions(element?.conditions, blockState) ? (<Grid
                                    item
                                    key={index}
                                    xs={element.xs ? element.xs : (12 / blockHeaders.length)}
                                    className={layoutClasses.gridItemMargins}
                                >
                                    {
                                        <Box className={layoutClasses.fullWidthBox}>
                                            <Grid container>
                                                <div className={layoutClasses.labelRow}>
                                                  {
                                                    element?.type !== "checkbox" ? (
                                                        <Tooltip
                                                            title={
                                                                element?.tooltipText ? (
                                                                    <span dangerouslySetInnerHTML={{ __html: element.tooltipText }} />
                                                                ) : ''
                                                            }
                                                            arrow
                                                        >
                                                            <Typography
                                                                component='span'
                                                                className={`${layoutClasses.fieldLabel} ${layoutClasses.fieldLabelClamp}`}
                                                            >
                                                                {element.label} : {element.isRequired ? '*' : ''}
                                                            </Typography>
                                                        </Tooltip>
                                                    ) : null
                                                  }
                                                    {element?.isHistory && element?.showHistroy && props?.match?.params?.id && blockState != null && (
                                                        <Tooltip title="View history" arrow>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => showHistory(element)}
                                                                style={{ padding: 2, marginLeft: 2 }}
                                                                aria-label="View decision history"
                                                            >
                                                                <HistoryIcon style={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </div>

                                                <div className={layoutClasses.fieldWrapper}>
                                                    <RenderFieldBasedOnType
                                                        {...props}
                                                        classes={layoutClasses}
                                                        element={isFieldReadOnly ? { ...element, readOnly: true } : element}
                                                        onChange={onChange}
                                                        handleCheckboxChange={handleCheckboxChange}
                                                        dataObj={blockState}
                                                        options={defaultOptions[element.value || element.name]}
                                                        selectedStep={selectedStep}
                                                        inputClasses={inputClasses}
                                                    />
                                                </div>
                                                {showValidationErrors && element.isRequired && element?.readOnly !== true && (() => {
                                                    const getValueFromPath = (obj, path) => {
                                                        if (!path) return undefined;
                                                        return path.split('.').reduce((acc, key) => acc?.[key], obj);
                                                    };
                                                    const rawValue = getValueFromPath(blockState, element.value) ?? blockState[element.name];
                                                    const isEmpty = isApiFetchSelectElement(element)
                                                        ? isApiFetchSelectValueEmpty(rawValue)
                                                        : rawValue === undefined || rawValue === null || rawValue === '' || (Array.isArray(rawValue) && rawValue.length === 0);
                                                    return isEmpty ? (
                                                        <Typography variant="caption" color="error" style={{ marginTop: 4, display: 'block' }}>
                                                            {element.label || 'This field'} is required
                                                        </Typography>
                                                    ) : null;
                                                })()}
                                                {(() => {
                                                    const tierDateConfig = block?.config?.tierDateValidation;
                                                    const mode = tierDateConfig?.mode === 'error' ? 'error' : DEFAULT_TIER_DATE_VALIDATION_MODE;
                                                    const rules = tierDateConfig?.rules ?? DEFAULT_TIER_DATE_RULES;
                                                    const tierDateMessage = getTierDateErrorForElement(blockState, element.value, rules);
                                                    if (!tierDateMessage) return null;
                                                    const isWarning = mode === 'warning';
                                                    return (
                                                        <Typography
                                                            variant="caption"
                                                            color={isWarning ? 'inherit' : 'error'}
                                                            style={{
                                                                marginTop: 4,
                                                                display: 'block',
                                                                ...(isWarning ? { color: '#ed6c02' } : {}),
                                                            }}
                                                        >
                                                            {tierDateMessage}
                                                        </Typography>
                                                    );
                                                })()}
                                                {tierResetFields.has(element.value) && hasDealDocumentContext && dealDocumentIdStr && (
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        startIcon={
                                                            isSaveDealReviewHistoryLoading ? null : (
                                                                <RotateLeftIcon style={{ fontSize: 14 }} />
                                                            )
                                                        }
                                                        disabled={isSaveDealReviewHistoryLoading}
                                                        onClick={() => handleTierReset()}
                                                        style={{
                                                            marginTop: 6,
                                                            textTransform: 'none',
                                                            fontSize: 11,
                                                            borderColor: '#d32f2f',
                                                            color: '#d32f2f',
                                                            ...(isSaveDealReviewHistoryLoading
                                                                ? {
                                                                      padding: '2px 10px',
                                                                      minHeight: 22,
                                                                      height: 22,
                                                                      minWidth: 40,
                                                                      lineHeight: 1,
                                                                      boxSizing: 'border-box',
                                                                  }
                                                                : {
                                                                      padding: '2px 10px',
                                                                      lineHeight: 1.6,
                                                                      minWidth: 72,
                                                                  }),
                                                        }}
                                                    >
                                                        {isSaveDealReviewHistoryLoading ? (
                                                            <CircularProgress size={16} thickness={5} style={{ color: '#d32f2f' }} />
                                                        ) : (
                                                            'Reset'
                                                        )}
                                                    </Button>
                                                )}
                                            </Grid>
                                        </Box>
                                    }
                                </Grid>) : null
                            )
                        }) : (
                            <Typography>{`No Fields are Configured`}</Typography>
                        )
                    }
                </Grid>
            </div>
             {
                decisionHistoryOpen.open && (
                    <Dialog
                        open={decisionHistoryOpen?.open}
                        onClose={() => {}}
                        maxWidth={false}
                        disableBackdropClick
                        disableEscapeKeyDown
                        PaperProps={{
                            className: dialogClasses.dialogPaper
                        }}
                    >
                        <DialogTitle className={dialogClasses.dialogTitle} disableTypography>
                            <Typography variant="h6" component="span">{`${decisionHistoryOpen?.data?.label ?? ''} History`}</Typography>
                        </DialogTitle>
                        <Divider />
                        <DialogContent className={dialogClasses.dialogContent}>
                            {(() => {
                                // Helper function to format date to DD/MM/YY
                                const formatDateToDDMMYY = (dateStr) => {
                                    if (!dateStr) return '';
                                    try {
                                        // Handle ISO date strings (YYYY-MM-DD) - parse directly to avoid timezone issues
                                        if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
                                            const parts = dateStr.split('T')[0].split('-');
                                            if (parts.length === 3) {
                                                return `${parts[2]}/${parts[1]}/${parts[0].slice(-2)}`;
                                            }
                                        }
                                        // For other date formats, parse as Date object
                                        const date = new Date(dateStr);
                                        if (isNaN(date.getTime())) return dateStr;
                                        const day = String(date.getDate()).padStart(2, '0');
                                        const month = String(date.getMonth() + 1).padStart(2, '0');
                                        const year = String(date.getFullYear()).slice(-2);
                                        return `${day}/${month}/${year}`;
                                    } catch {
                                        return dateStr;
                                    }
                                };

                                // Helper function to parse entry and extract old/new dates
                                const parseEntryValue = (entry) => {
                                    if (!entry || typeof entry !== 'object') return null;
                                    
                                    // Check if entry has from/to dates
                                    if (entry.fromValue && entry.toValue) {
                                        const oldDate = formatDateToDDMMYY(entry.fromValue);
                                        const newDate = formatDateToDDMMYY(entry.toValue);
                                        return {
                                            text: `Updated Target Completion Date From ${oldDate} to ${newDate}`,
                                            oldDate,
                                            newDate
                                        };
                                    }
                                    
                                    // Check if entry.value contains date information
                                    if (entry.value) {
                                        // Try to parse if it's a date string or contains date info
                                        return {
                                            text: entry.value,
                                            oldDate: null,
                                            newDate: null
                                        };
                                    }
                                    
                                    return null;
                                };

                                const fromState = Array.isArray(decisionHistoryOpen?.data?.history) ? decisionHistoryOpen?.data?.history : [];
                                const items = fromState.length > 0 ? fromState : [];
                                
                                if (items.length === 0) {
                                    return (
                                        <Box display="flex" alignItems="center" justifyContent="center" py={3}>
                                            <Typography color="textSecondary">No history available.</Typography>
                                        </Box>
                                    );
                                }
                                
                                return (
                                    <List className={dialogClasses.listRoot} dense>
                                        {items.map((entry, idx) => {
                                            const isTracked = typeof entry === 'object' && entry != null && 'value' in entry;
                                            if (isTracked) {
                                                const timeStr = formatDecisionHistoryDisplayTime(entry);
                                                const byStr = entry.updatedBy ? entry.updatedBy : '';
                                                const parsedEntry = parseEntryValue(entry);
                                                
                                                // Format the primary text with bold dates (matching Figma: "From" and "to" are also bolded)
                                                let primaryText = parsedEntry?.text || entry.value || '';
                                                if (parsedEntry?.oldDate && parsedEntry?.newDate) {
                                                    primaryText = (
                                                        <>
                                                            Updated Target Completion Date <strong>From {parsedEntry.oldDate} to {parsedEntry.newDate}</strong>
                                                        </>
                                                    );
                                                }
                                                
                                                return (
                                                    <React.Fragment key={idx}>
                                                        {idx > 0 && <Divider component="li" />}
                                                        <ListItem className={dialogClasses.listItem}>
                                                            <Typography className={dialogClasses.timestamp}>
                                                                {timeStr}
                                                            </Typography>
                                                            <Box className={dialogClasses.avatarContainer}>
                                                                <Avatar className={dialogClasses.avatar}>
                                                                    {byStr ? nameInitials(byStr) : 'U'}
                                                                </Avatar>
                                                            </Box>
                                                            <ListItemText
                                                                className={dialogClasses.listItemText}
                                                                primary={primaryText}
                                                                secondary={byStr ? `By ${byStr}` : ''}
                                                                primaryTypographyProps={{ variant: 'body1' }}
                                                                secondaryTypographyProps={{ variant: 'caption', color: 'textSecondary' }}
                                                            />
                                                        </ListItem>
                                                    </React.Fragment>
                                                );
                                            }
                                            return null;
                                        })}
                                    </List>
                                );
                            })()}
                        </DialogContent>
                        <Divider />
                        <DialogActions className={dialogClasses.dialogActions}>
                            <Button
                                variant="contained"
                                onClick={() => setDecisionHistoryOpen({ open: false, data: {
                                    value: null,
                                    key: null,
                                    label: null,
                                    history: null
                                } })}
                                className={dialogClasses.closeButton}
                            >
                                CLOSE
                            </Button>
                        </DialogActions>
                    </Dialog>
                )
            }
        </React.Fragment>
    )
}

export default SummaryBlock
